import { useMemo, useState, type ReactNode } from 'react';
import { useLazyGetArtefactQuery } from '@/services/api';
import type {
  ArchitectOutput,
  CoderFile,
  CoderOutput,
  DiffLine,
  DocumenterOutput,
  ExplorerOutput,
  FindingType,
  ReviewerOutput,
  RoleOutput,
  TesterOutput,
  IssueSeverity,
  DeterminismClassification
} from '@/types/api';


function splitSummaryPoints(summary: string): string[] {
  const lines = summary
    .split(/\r?\n/)
    .map((l) => l.replace(/^\s*(?:\d+[.)]|[-*•])\s*/, '').trim())
    .filter((l) => l.length > 0);
  if (lines.length >= 2) return lines;
  const inline = summary
    .split(/\s*(?:(?<=\S)\s)?\d+[.)]\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (inline.length >= 2) return inline;
  return [];
}

const ROLE_PILL_CLASS: Record<string, string> = {
  explorer: 'bg-role-explorer text-white',
  architect: 'bg-role-architect text-white',
  coder: 'bg-role-coder text-white',
  reviewer: 'bg-role-reviewer text-white',
  tester: 'bg-role-tester text-white',
  documenter: 'bg-role-documenter text-white'
};

export function RoleHeader({
  role,
  title
}: {
  role: string;
  title: string;
}): JSX.Element {
  const cls = ROLE_PILL_CLASS[role] ?? 'bg-panel-2 text-text';
  return (
    <div
      className="flex items-center gap-2.5 mb-3 pb-2.5 border-b border-border"
      data-testid={`renderer-head-${role}`}
    >
      <span className={`text-[11px] px-2 py-0.5 rounded-[10px] font-medium capitalize ${cls}`}>
        {role}
      </span>
      <span className="font-semibold text-[13.5px]">{title}</span>
    </div>
  );
}


export function ExplorerRenderer({ output }: { output: ExplorerOutput }): JSX.Element {
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<FindingType | 'all'>('all');
  const [sortKey, setSortKey] = useState<'file_path' | 'line_number' | 'finding_type'>(
    'file_path'
  );

  const filtered = useMemo(() => {
    let rows = [...output.findings];
    const q = filter.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (f) =>
          f.file_path.toLowerCase().includes(q) ||
          f.summary.toLowerCase().includes(q) ||
          f.finding_type.toLowerCase().includes(q)
      );
    }
    if (typeFilter !== 'all') {
      rows = rows.filter((f) => f.finding_type === typeFilter);
    }
    rows.sort((a, b) => {
      if (sortKey === 'line_number') return a.line_number - b.line_number;
      return String(a[sortKey]).localeCompare(String(b[sortKey]));
    });
    return rows;
  }, [output.findings, filter, typeFilter, sortKey]);

  const presentTypes = useMemo(
    () => Array.from(new Set(output.findings.map((f) => f.finding_type))).sort(),
    [output.findings]
  );

  const pillClass: Record<FindingType, string> = {
    file_discovery: 'bg-role-explorer/15 text-role-explorer',
    pattern_match: 'bg-warn/15 text-warn',
    call_chain_node: 'bg-text-dim/15 text-text-dim',
    entity: 'bg-role-explorer/15 text-role-explorer',
    module: 'bg-role-architect/15 text-role-architect',
    convention: 'bg-text-dim/15 text-text-dim',
    gap: 'bg-warn/15 text-warn',
    risk: 'bg-err/15 text-err'
  };

  return (
    <div data-testid="role-explorer">
      <RoleHeader role="explorer" title={`Findings — ${output.findings.length} items`} />
      {output.analysis_mode ? (
        <div
          data-testid="explorer-mode-banner"
          className="mb-3 px-3 py-2 rounded-md text-[12px] bg-role-explorer/10 text-role-explorer border border-role-explorer/30"
        >
          {output.analysis_mode === 'requirement'
            ? 'No source repository for this run — the Explorer analysed the submitted requirement document (entities, modules, conventions, gaps, risks).'
            : output.analysis_mode === 'codebase+requirement'
              ? 'Existing repository scanned AND the requirement analysed — findings cover both the codebase (files, patterns, call chains) and the requirement (entities, modules, conventions, gaps, risks).'
              : 'Source repository scanned — findings are drawn from the existing codebase (files, patterns, call chains).'}
        </div>
      ) : null}
      {output.summary
        ? (() => {
            const points = splitSummaryPoints(output.summary);
            return points.length >= 2 ? (
              <ol
                data-testid="explorer-summary"
                className="mb-3 p-2.5 pl-6 bg-panel-2 border border-border rounded-md text-[12.5px] leading-relaxed text-text-dim list-decimal space-y-1"
              >
                {points.map((pt, i) => (
                  <li key={i} className="pl-1">
                    {pt}
                  </li>
                ))}
              </ol>
            ) : (
              <p
                data-testid="explorer-summary"
                className="mb-3 p-2.5 bg-panel-2 border border-border rounded-md text-[12.5px] leading-relaxed text-text-dim whitespace-pre-wrap"
              >
                {output.summary}
              </p>
            );
          })()
        : null}
      <div className="flex gap-2 mb-2.5">
        <input
          aria-label="Filter findings"
          autoComplete="off-findings-filter"
          placeholder="Filter findings…"
          className="flex-1 h-[28px] px-2.5 bg-panel-2 border border-border rounded-md text-text text-xs"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <select
          aria-label="Filter finding type"
          className="h-[28px] px-2.5 bg-panel-2 border border-border rounded-md text-text text-xs"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as FindingType | 'all')}
        >
          <option value="all">All types</option>
          {presentTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full table-fixed border-collapse text-[12.5px]" data-testid="explorer-table">
        <thead>
          <tr>
            <ThSort label="file_path" col="file_path" sortKey={sortKey} onSort={setSortKey} width="35%" />
            <ThSort label="line" col="line_number" sortKey={sortKey} onSort={setSortKey} width="90px" />
            <ThSort label="finding_type" col="finding_type" sortKey={sortKey} onSort={setSortKey} width="120px" />
            <th className="text-left p-2 bg-panel-2 text-text-muted font-medium text-[11px] uppercase tracking-wider border-b border-border">
              summary
            </th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((f, i) => (
            <tr
              key={`${f.finding_type}:${f.file_path}:${f.line_number}:${i}`}
              data-testid="explorer-row"
            >
              <td className="p-2 border-b border-border font-mono text-[11.5px] text-text break-words">
                {f.file_path}
              </td>
              <td className="p-2 border-b border-border text-text-dim">{f.line_number}</td>
              <td className="p-2 border-b border-border">
                <span
                  className={`inline-block px-1.5 text-[10.5px] rounded font-medium ${pillClass[f.finding_type]}`}
                >
                  {f.finding_type}
                </span>
              </td>
              <td className="p-2 border-b border-border text-text-dim">{f.summary}</td>
            </tr>
          ))}
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={4} className="p-3 text-center text-text-muted text-[11.5px]">
                No findings match the current filter.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function ThSort({
  label,
  col,
  sortKey,
  onSort,
  width
}: {
  label: string;
  col: 'file_path' | 'line_number' | 'finding_type';
  sortKey: string;
  onSort: (k: 'file_path' | 'line_number' | 'finding_type') => void;
  width?: string;
}): JSX.Element {
  const active = sortKey === col;
  return (
    <th
      style={width ? { width } : undefined}
      onClick={() => onSort(col)}
      className="text-left p-2 bg-panel-2 text-text-muted font-medium text-[11px] uppercase tracking-wider border-b border-border cursor-pointer"
    >
      {label} <span className={active ? 'text-accent' : 'opacity-40'}>{active ? '↑' : '↕'}</span>
    </th>
  );
}


export function ArchitectRenderer({ output }: { output: ArchitectOutput }): JSX.Element {
  const ordered = useMemo(() => {
    const byId = new Map(output.tasks.map((t) => [t.task_id, t]));
    const seen = new Set<string>();
    const out: ArchitectOutput['tasks'][number][] = [];
    for (const id of output.execution_schedule) {
      const t = byId.get(id);
      if (t && !seen.has(id)) {
        out.push(t);
        seen.add(id);
      }
    }
    for (const t of output.tasks) if (!seen.has(t.task_id)) out.push(t);
    return out;
  }, [output.tasks, output.execution_schedule]);

  const posOf = useMemo(() => {
    const m = new Map<string, number>();
    ordered.forEach((t, i) => m.set(t.task_id, i + 1));
    return m;
  }, [ordered]);

  return (
    <div data-testid="role-architect">
      <RoleHeader role="architect" title={`Plan — ${output.tasks.length} tasks`} />

      {output.analysis_summary ? (
        <p className="text-[12.5px] text-text-dim leading-relaxed mb-3" data-testid="arch-summary">
          {output.analysis_summary}
        </p>
      ) : null}

      <DependencyGraph tasks={output.tasks} />

      {output.execution_schedule.length > 0 ? (
        <ExecutionSchedule order={output.execution_schedule} />
      ) : null}

      {output.acceptance_criteria.length > 0 ? (
        <div className="mb-3" data-testid="arch-run-acs">
          <SectionLabel>
            Acceptance criteria — the tested requirements ({output.acceptance_criteria.length})
          </SectionLabel>
          <ul className="list-disc pl-4 space-y-0.5">
            {output.acceptance_criteria.map((ac, i) => (
              <li key={i} className="text-[12px] text-text-dim leading-snug">
                {ac}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <SectionLabel>Tasks — run in this order ({ordered.length})</SectionLabel>
      <div className="mb-3">
        {ordered.map((t, i) => (
          <ArchitectTaskRow key={t.task_id} task={t} index={i + 1} posOf={posOf} />
        ))}
      </div>

      {output.plan_markdown ? (
        <details className="mb-2" data-testid="arch-plan-md">
          <summary className="text-[11px] text-text-muted uppercase tracking-wider cursor-pointer mb-1.5">
            Raw plan (plan.md)
          </summary>
          <pre className="text-[11.5px] leading-relaxed whitespace-pre-wrap break-words p-2.5 border border-border rounded-md bg-panel-2 text-text-dim overflow-x-auto">
            {output.plan_markdown}
          </pre>
        </details>
      ) : null}
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1.5 mt-1">{children}</div>
  );
}

function ExecutionSchedule({ order }: { order: ReadonlyArray<string> }): JSX.Element {
  return (
    <div className="mb-3" data-testid="arch-schedule">
      <SectionLabel>Execution order</SectionLabel>
      <div className="flex flex-wrap items-center gap-1">
        {order.map((id, i) => (
          <span key={id} className="flex items-center gap-1">
            <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-role-architect/15 text-role-architect">
              {id}
            </span>
            {i < order.length - 1 ? <span className="text-text-muted text-[11px]">→</span> : null}
          </span>
        ))}
      </div>
    </div>
  );
}

function DependencyGraph({
  tasks
}: {
  tasks: ReadonlyArray<ArchitectOutput['tasks'][number]>;
}): JSX.Element | null {
  const layout = useMemo(() => {
    const byId = new Map(tasks.map((t) => [t.task_id, t]));
    const depth = new Map<string, number>();
    const visiting = new Set<string>();
    const depthOf = (id: string): number => {
      if (depth.has(id)) return depth.get(id)!;
      if (visiting.has(id)) return 0;
      visiting.add(id);
      const t = byId.get(id);
      const preds = t ? t.depends_on.filter((p) => byId.has(p)) : [];
      const d = preds.length === 0 ? 0 : 1 + Math.max(...preds.map(depthOf));
      visiting.delete(id);
      depth.set(id, d);
      return d;
    };
    tasks.forEach((t) => depthOf(t.task_id));

    const cols = new Map<number, string[]>();
    tasks.forEach((t) => {
      const d = depth.get(t.task_id) ?? 0;
      cols.set(d, [...(cols.get(d) ?? []), t.task_id]);
    });

    const COL_W = 92;
    const ROW_H = 40;
    const NODE_W = 60;
    const NODE_H = 24;
    const PAD = 12;
    const pos = new Map<string, { x: number; y: number }>();
    cols.forEach((ids, d) => {
      ids.forEach((id, row) => {
        pos.set(id, { x: PAD + d * COL_W, y: PAD + row * ROW_H });
      });
    });
    const maxDepth = Math.max(0, ...Array.from(cols.keys()));
    const maxRows = Math.max(1, ...Array.from(cols.values()).map((a) => a.length));
    const width = PAD * 2 + maxDepth * COL_W + NODE_W;
    const height = PAD * 2 + (maxRows - 1) * ROW_H + NODE_H;
    return { pos, width, height, NODE_W, NODE_H };
  }, [tasks]);

  if (tasks.length === 0) return null;
  const { pos, width, height, NODE_W, NODE_H } = layout;

  return (
    <div className="mb-3" data-testid="arch-graph">
      <SectionLabel>Dependency graph</SectionLabel>
      <div className="bg-surface border border-border rounded-md p-2 overflow-x-auto">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width={width}
          height={height}
          className="max-w-full"
          data-testid="arch-graph-svg"
          role="img"
          aria-label="Task dependency graph"
        >
          <defs>
            <marker
              id="dep-arrow"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M0,0 L10,5 L0,10 z" fill="var(--role-architect)" />
            </marker>
          </defs>
          {tasks.flatMap((t) =>
            t.depends_on.map((dep) => {
              const a = pos.get(dep);
              const b = pos.get(t.task_id);
              if (!a || !b) return null;
              return (
                <line
                  key={`${dep}->${t.task_id}`}
                  x1={a.x + NODE_W}
                  y1={a.y + NODE_H / 2}
                  x2={b.x}
                  y2={b.y + NODE_H / 2}
                  stroke="var(--role-architect)"
                  strokeOpacity={0.45}
                  strokeWidth={1.25}
                  markerEnd="url(#dep-arrow)"
                />
              );
            })
          )}
          {tasks.map((t) => {
            const p = pos.get(t.task_id);
            if (!p) return null;
            return (
              <g key={t.task_id}>
                <title>{`${t.task_id} — ${t.title}`}</title>
                <rect
                  x={p.x}
                  y={p.y}
                  width={NODE_W}
                  height={NODE_H}
                  rx={5}
                  fill="var(--surface)"
                  stroke="var(--role-architect)"
                  strokeWidth={1.25}
                />
                <text
                  x={p.x + NODE_W / 2}
                  y={p.y + NODE_H / 2}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontFamily="ui-monospace, Menlo, monospace"
                  fontSize={11}
                  fill="var(--role-architect)"
                >
                  {t.task_id}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function ArchitectTaskRow({
  task,
  index,
  posOf
}: {
  task: ArchitectOutput['tasks'][number];
  index: number;
  posOf: Map<string, number>;
}): JSX.Element {
  const afterPositions = task.depends_on
    .map((id) => posOf.get(id))
    .filter((p): p is number => typeof p === 'number')
    .sort((a, b) => a - b);
  return (
    <div
      className="flex items-start gap-2.5 p-2.5 border border-border rounded-md mb-2 bg-panel-2"
      data-testid="arch-task"
    >
      <span className="font-mono text-[11.5px] text-role-architect w-[24px] flex-none pt-px text-right">
        {index}.
      </span>
      <span className="flex-1 text-[12.5px]">
        <span className="font-mono text-[11px] text-text-muted mr-1.5">{task.task_id}</span>
        {task.title}
        {afterPositions.length > 0 ? (
          <span className="text-text-muted"> (after {afterPositions.join(', ')})</span>
        ) : null}
        {task.target_files.length > 0 ? (
          <div className="mt-1 flex flex-wrap gap-1" data-testid="arch-task-files">
            {task.target_files.map((f) => (
              <span
                key={f}
                className="font-mono text-[10.5px] px-1 py-px rounded bg-panel-3 text-text-muted"
              >
                {f}
              </span>
            ))}
          </div>
        ) : null}
        {task.acceptance_criteria.length > 0 ? (
          <div className="mt-1.5" data-testid="arch-task-scenarios">
            <div className="text-[10.5px] text-text-muted uppercase tracking-wider mb-0.5">
              Scenarios
            </div>
            <ul className="list-disc pl-4 space-y-0.5">
              {task.acceptance_criteria.map((ac, i) => (
                <li key={i} className="text-[12px] text-text-dim leading-snug">
                  {ac}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="mt-1 text-[11px] text-text-muted italic" data-testid="arch-task-buildonly">
            Build/wiring task — no test scenarios.
          </div>
        )}
      </span>
    </div>
  );
}


export function CoderRenderer({ output }: { output: CoderOutput }): JSX.Element {
  const [activeIdx, setActiveIdx] = useState(0);
  const file = output.files[activeIdx] ?? output.files[0];
  if (!file) {
    return (
      <div data-testid="role-coder">
        <RoleHeader role="coder" title="Diff — no files changed" />
        <CoderSelfVerifyPanel output={output} />
        <div className="text-text-muted text-[12.5px] p-3">No file diffs to display.</div>
      </div>
    );
  }
  return (
    <div data-testid="role-coder">
      <RoleHeader role="coder" title={`Diff — ${output.files.length} files changed`} />
      <CoderSelfVerifyPanel output={output} />
      <div className="flex gap-3 items-start">
        <div
          className="w-64 shrink-0 max-h-[70vh] overflow-auto bg-panel-2 border border-border rounded-md p-1.5"
          data-testid="coder-file-tree"
        >
          <CoderFileTree files={output.files} activeIdx={activeIdx} onSelect={setActiveIdx} />
        </div>
        <div className="flex-1 min-w-0">
          <CoderDiffPane file={file} />
        </div>
      </div>
    </div>
  );
}

function CoderSelfVerifyPanel({ output }: { output: CoderOutput }): JSX.Element | null {
  const sv = output.self_verification;
  const gate = output.verification_gate;
  if (!sv && !gate) return null;
  const tr = sv?.final_test_results;
  const verdictPass = sv ? sv.passed : Boolean(gate?.passed);
  const noToolchain = gate ? !gate.evidence_available : false;
  const passedNothingMeasured =
    Boolean(sv?.passed) && (sv?.final_test_results?.tests_total ?? 0) === 0;
  const nothingMeasured =
    (Boolean(sv?.advisory_unverified) || passedNothingMeasured) && !noToolchain;
  const advisory = noToolchain || nothingMeasured;
  return (
    <div
      className={`mb-3 rounded border px-3 py-2 ${
        verdictPass ? 'border-ok/40 bg-ok/10' : 'border-err/40 bg-err/10'
      }`}
      data-testid="coder-self-verification"
    >
      <div className="flex items-center gap-2 text-xs font-semibold">
        <span className={verdictPass ? 'text-ok' : 'text-err'}>
          {verdictPass ? '✓ Self-verification passed' : '✗ Self-verification FAILED'}
        </span>
        {advisory ? (
          <span className="text-warn text-[11px]">
            {noToolchain
              ? '(unverified — no runnable toolchain)'
              : '(unverified — no tests were measured)'}
          </span>
        ) : null}
        {sv && sv.loops_run > 0 ? (
          <span className="text-text-muted text-[11px]">
            · {sv.loops_run}
            {sv.max_loops !== undefined ? ` of ${sv.max_loops}` : ''} fix{' '}
            {sv.loops_run === 1 ? 'attempt' : 'attempts'}
            {sv.total_loops_run !== undefined && sv.total_loops_run > sv.loops_run
              ? ` · ${sv.total_loops_run} across the run`
              : ''}
          </span>
        ) : null}
      </div>
      {tr && tr.evidence_available && tr.tests_total > 0 ? (
        <div className="mt-1 text-[11px] text-text-muted">
          tests: <span className="text-ok">{tr.tests_passed} passed</span>
          {tr.tests_failed > 0 ? (
            <>
              {' · '}
              <span className="text-err">{tr.tests_failed} failed</span>
            </>
          ) : null}
          {' '}
          of {tr.tests_total}
        </div>
      ) : null}
      {!verdictPass && sv?.final_gate_reason ? (
        <p className="mt-1 text-[11px] text-text-muted font-mono break-words">
          {sv.final_gate_reason}
        </p>
      ) : null}
      {tr && tr.failing_cases.length > 0 ? (
        <ul className="mt-1.5 flex flex-col gap-0.5" data-testid="coder-self-verify-failing">
          {tr.failing_cases.slice(0, 20).map((name, i) => (
            <li key={`${name}-${i}`} className="text-[11px] text-err font-mono">
              ✗ {name}
            </li>
          ))}
        </ul>
      ) : null}
      {sv?.structured_analysis && sv.structured_analysis.findings.length > 0 ? (
        <div className="mt-2 flex flex-col gap-2" data-testid="coder-self-verify-findings">
          {sv.structured_analysis.source === 'fallback_raw' ? (
            <span className="text-[10.5px] text-text-muted italic">
              (automatic structuring unavailable — showing raw runner output)
            </span>
          ) : null}
          {sv.structured_analysis.findings.slice(0, 15).map((f, i) => (
            <div
              key={`finding-${i}`}
              className="rounded border border-border bg-panel-2 p-2 text-[11px]"
              data-testid="coder-self-verify-finding"
            >
              <div className="flex items-center gap-2">
                <span className="uppercase text-[9.5px] font-semibold tracking-wide text-err">
                  {f.severity}
                </span>
                <span className="font-semibold text-text">{f.what}</span>
              </div>
              {f.why ? <div className="mt-0.5 text-text-muted">why: {f.why}</div> : null}
              {f.expected ? (
                <div className="mt-0.5 text-text-muted">
                  <span className="font-semibold">expected (test):</span> {f.expected}
                </div>
              ) : null}
              {f.actual ? (
                <div className="mt-0.5 text-text-muted">
                  <span className="font-semibold">actual (code):</span> {f.actual}
                </div>
              ) : null}
              {f.suggested_fix ? (
                <div className="mt-0.5 text-text">fix: {f.suggested_fix}</div>
              ) : null}
              {f.file ? (
                <div className="mt-0.5 font-mono text-text-muted">{f.file}</div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {gate && gate.steps.length > 0 ? (
        <div className="mt-2 flex flex-col gap-0.5">
          {gate.steps.map((s, i) => (
            <div
              key={`${s.name}-${i}`}
              className="grid grid-cols-[16px_90px_1fr_70px] gap-2 items-center text-[11px]"
              data-testid="coder-self-verify-step"
            >
              <span className={s.skipped ? 'text-text-muted' : s.passed ? 'text-ok' : 'text-err'}>
                {s.skipped ? '○' : s.passed ? '✓' : '✗'}
              </span>
              <span className="font-semibold uppercase">{s.name}</span>
              <span className="font-mono text-text-muted truncate">{s.command}</span>
              <span className="text-text-muted text-right">
                {s.skipped ? 'skipped' : s.exit_code === null ? '—' : `exit ${s.exit_code}`}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface CoderTreeNode {
  name: string;
  children: Map<string, CoderTreeNode>;
  fileIdx?: number;
  filePath?: string;
}

function buildCoderTree(files: ReadonlyArray<CoderFile>): CoderTreeNode {
  const root: CoderTreeNode = { name: '', children: new Map() };
  files.forEach((f, idx) => {
    const parts = f.file_path.split('/').filter((p) => p.length > 0);
    let cur = root;
    parts.forEach((part, i) => {
      let next = cur.children.get(part);
      if (!next) {
        next = { name: part, children: new Map() };
        cur.children.set(part, next);
      }
      if (i === parts.length - 1) {
        next.fileIdx = idx;
        next.filePath = f.file_path;
      }
      cur = next;
    });
  });
  return root;
}

function CoderFileTree({
  files,
  activeIdx,
  onSelect
}: {
  files: ReadonlyArray<CoderFile>;
  activeIdx: number;
  onSelect: (idx: number) => void;
}): JSX.Element {
  const root = buildCoderTree(files);
  return (
    <ul className="text-[11.5px]">
      {[...root.children.values()].map((node) => (
        <CoderTreeRow key={node.name} node={node} depth={0} activeIdx={activeIdx} onSelect={onSelect} />
      ))}
    </ul>
  );
}

function CoderTreeRow({
  node,
  depth,
  activeIdx,
  onSelect
}: {
  node: CoderTreeNode;
  depth: number;
  activeIdx: number;
  onSelect: (idx: number) => void;
}): JSX.Element {
  const isFile = node.fileIdx !== undefined;
  const [open, setOpen] = useState(true);
  const pad = { paddingLeft: `${depth * 12 + 6}px` };
  if (isFile) {
    const active = node.fileIdx === activeIdx;
    return (
      <li>
        <button
          type="button"
          style={pad}
          onClick={() => onSelect(node.fileIdx!)}
          className={`w-full text-left font-mono py-0.5 pr-2 rounded ${
            active ? 'bg-panel-3 text-text' : 'text-text-dim hover:text-text'
          }`}
          data-testid={`coder-file-tab-${node.filePath ?? node.name}`}
        >
          {node.name}
        </button>
      </li>
    );
  }
  return (
    <li>
      <button
        type="button"
        style={pad}
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left py-0.5 pr-2 text-text-muted hover:text-text"
      >
        <span className="inline-block w-3">{open ? '▾' : '▸'}</span>
        {node.name}/
      </button>
      {open ? (
        <ul>
          {[...node.children.values()].map((child) => (
            <CoderTreeRow key={child.name} node={child} depth={depth + 1} activeIdx={activeIdx} onSelect={onSelect} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function CoderDiffPane({ file }: { file: CoderFile }): JSX.Element {
  const [trigger, result] = useLazyGetArtefactQuery();
  const [expanded, setExpanded] = useState(false);
  const expandedBody = result.data ?? null;

  return (
    <div data-testid="coder-diff-pane">
      {file.handle && file.handle.preview_truncated ? (
        <div
          className="flex items-center gap-3 px-3.5 py-3 mb-3 rounded-md border border-warn/30 bg-warn/10"
          role="status"
          data-testid="trunc-banner"
        >
          <span className="text-warn text-base">📎</span>
          <div className="flex-1 text-[12.5px] text-warn">
            <strong>Artefact replaced with reference handle.</strong> Inline preview shows{' '}
            {file.handle.inline_bytes.toLocaleString()} of{' '}
            {file.handle.full_bytes.toLocaleString()} bytes. Full diff fetched on demand.
          </div>
          <button
            type="button"
            disabled={result.isFetching || expanded}
            onClick={() => {
              if (file.handle) {
                void trigger(file.handle.ref).then(() => setExpanded(true));
              }
            }}
            className="px-3 py-1 bg-warn text-[#1a1f2c] border-0 rounded-md text-[11.5px] font-semibold cursor-pointer disabled:opacity-60"
            data-testid="load-full-content"
          >
            {result.isFetching ? 'Loading…' : expanded ? 'Loaded' : 'Load full content'}
          </button>
        </div>
      ) : null}
      <div
        className="bg-panel border border-border rounded-md max-h-[70vh] overflow-y-auto overflow-x-hidden font-mono text-[11.5px] leading-relaxed"
        data-testid="coder-diff"
      >
        {file.diff.map((line, i) => (
          <DiffRow key={i} line={line} />
        ))}
        {expanded && expandedBody ? (
          <div className="px-3 py-2 text-text-muted text-[11px] border-t border-border" data-testid="artefact-body">
            <div className="mb-1.5 uppercase tracking-wider">Full artefact body</div>
            <pre className="whitespace-pre-wrap">{expandedBody}</pre>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DiffRow({ line }: { line: DiffLine }): JSX.Element {
  const cls =
    line.kind === 'add'
      ? 'bg-ok/10'
      : line.kind === 'del'
        ? 'bg-err/10'
        : '';
  const prefix = line.kind === 'add' ? '+ ' : line.kind === 'del' ? '- ' : '  ';
  return (
    <>
      <div className="grid grid-cols-[40px_40px_1fr]" data-testid={`diff-row-${line.kind}`}>
        <span className="text-text-muted text-right px-2 bg-panel">{line.old_line ?? ''}</span>
        <span className="text-text-muted text-right px-2 bg-panel">{line.new_line ?? ''}</span>
        <span className={`px-3 whitespace-pre-wrap break-words [overflow-wrap:anywhere] ${cls}`}>
          <span className={line.kind === 'add' ? 'text-ok' : line.kind === 'del' ? 'text-err' : 'text-text-muted'}>
            {prefix}
          </span>
          {line.text}
        </span>
      </div>
      {line.inline_comment ? (
        <div
          className="px-3.5 py-2 text-[12px] text-text-dim border-l-[3px] border-warn bg-warn/10"
          data-testid="inline-comment"
        >
          ↳ {line.inline_comment}
        </div>
      ) : null}
    </>
  );
}


export function ReviewerRenderer({ output }: { output: ReviewerOutput }): JSX.Element {
  const grouped = useMemo(() => {
    const out = new Map<string, Map<IssueSeverity, ReviewerOutput['issues'][number][]>>();
    output.issues.forEach((i) => {
      const cat = out.get(i.category) ?? new Map();
      const arr = cat.get(i.severity) ?? [];
      arr.push(i);
      cat.set(i.severity, arr);
      out.set(i.category, cat);
    });
    return out;
  }, [output.issues]);

  const SEV_ORDER: IssueSeverity[] = ['Critical', 'High', 'Medium', 'Low'];
  const SEV_CLASS: Record<IssueSeverity, string> = {
    Critical: 'bg-err/20 text-err',
    High: 'bg-err/15 text-err',
    Medium: 'bg-warn/20 text-warn',
    Low: 'bg-text-dim/15 text-text-dim'
  };

  const lineLabel = (issue: ReviewerOutput['issues'][number]): string =>
    issue.line_end > issue.line_start
      ? `${issue.line_start}–${issue.line_end}`
      : String(issue.line_start);

  return (
    <div data-testid="role-reviewer">
      <RoleHeader
        role="reviewer"
        title={`Issues — ${output.issues.length} items · grouped by category × severity`}
      />
      {output.ledger ? (
        <div
          className="flex flex-wrap gap-2 items-center mb-3 text-[11px]"
          data-testid="reviewer-ledger-header"
        >
          <span className="px-2 py-0.5 rounded font-semibold uppercase tracking-wider bg-ok/15 text-ok">
            Resolved · {output.ledger.resolved_count}
          </span>
          <span className="px-2 py-0.5 rounded font-semibold uppercase tracking-wider bg-warn/20 text-warn">
            Remaining · {output.ledger.remaining_count}
          </span>
        </div>
      ) : null}
      {output.verdict || output.summary ? (
        <div className="flex flex-wrap gap-2 items-center mb-4" data-testid="reviewer-verdict">
          {output.verdict ? (
            <span
              className={`text-[11px] px-2 py-0.5 rounded font-semibold uppercase tracking-wider ${
                output.verdict === 'approved' ? 'bg-ok/15 text-ok' : 'bg-warn/20 text-warn'
              }`}
            >
              {output.verdict.replace(/_/g, ' ')}
            </span>
          ) : null}
          {output.summary
            ? SEV_ORDER.filter((s) => (output.summary?.by_severity[s] ?? 0) > 0).map((s) => (
                <span
                  key={s}
                  className={`text-[10.5px] px-1.5 rounded font-semibold uppercase ${SEV_CLASS[s]}`}
                >
                  {s} · {output.summary?.by_severity[s] ?? 0}
                </span>
              ))
            : null}
        </div>
      ) : null}

      {output.issues.length === 0 ? (
        <div className="text-sm text-text-dim italic" data-testid="reviewer-clean">
          No issues found — the Reviewer reported a clean change.
        </div>
      ) : null}

      {Array.from(grouped.entries()).map(([cat, severities]) => (
        <div key={cat} className="mb-4" data-testid={`issue-group-${cat}`}>
          <div className="flex gap-2.5 items-center text-[11.5px] text-text-muted uppercase tracking-wider mb-2 pb-1.5 border-b border-border">
            <span>{cat}</span>
            {SEV_ORDER.filter((s) => severities.has(s)).map((s) => (
              <span key={s} className={`text-[10.5px] px-1.5 rounded font-semibold uppercase ${SEV_CLASS[s]}`}>
                {s} · {severities.get(s)?.length ?? 0}
              </span>
            ))}
          </div>
          {SEV_ORDER.flatMap((s) =>
            (severities.get(s) ?? []).map((issue) => (
              <div
                key={issue.issue_id}
                className={`bg-panel-2 border border-border rounded-md p-3 mb-2 ${
                  issue.status === 'fixed' ? 'opacity-60' : ''
                }`}
                data-testid="reviewer-issue"
                data-status={issue.status ?? 'open'}
              >
                <div className="flex gap-2.5 items-center mb-1.5 flex-wrap">
                  {issue.status ? (
                    <span
                      className={`text-[10.5px] px-1.5 rounded font-semibold uppercase ${
                        issue.status === 'fixed' ? 'bg-ok/15 text-ok' : 'bg-warn/20 text-warn'
                      }`}
                      data-testid="reviewer-issue-status"
                    >
                      {issue.status === 'fixed' ? 'Fixed' : issue.status === 'new' ? 'New' : 'Open'}
                    </span>
                  ) : null}
                  <span
                    className={`text-[10.5px] px-1.5 rounded font-semibold uppercase ${SEV_CLASS[s]}`}
                  >
                    {s}
                  </span>
                  {issue.catalogue_entry_id ? (
                    <span className="text-[10.5px] px-1.5 rounded font-semibold uppercase bg-panel-3 text-text-muted">
                      {issue.catalogue_entry_id}
                    </span>
                  ) : null}
                  <span className="font-mono text-[11px] text-text-muted">
                    {issue.line_start > 0 ? `${issue.file_path}:${lineLabel(issue)}` : issue.file_path}
                  </span>
                </div>
                {issue.impact ? (
                  <div
                    className={`text-[12.5px] text-text ${
                      issue.status === 'fixed' ? 'line-through' : ''
                    }`}
                  >
                    {issue.impact}
                  </div>
                ) : null}
                {issue.reproduction_steps.length > 0 ? (
                  <div className="mt-2" data-testid="repro-steps">
                    <div className="text-[10.5px] uppercase tracking-wider text-text-muted mb-1">
                      Reproduction
                    </div>
                    <ol className="list-decimal list-inside text-xs text-text-dim space-y-0.5">
                      {issue.reproduction_steps.map((step, i) => (
                        <li key={i}>{step}</li>
                      ))}
                    </ol>
                  </div>
                ) : null}
                {issue.suggested_fix ? (
                  <div className="mt-2" data-testid="suggested-fix">
                    <span className="text-[10.5px] uppercase tracking-wider text-ok mr-1.5">
                      Suggested fix
                    </span>
                    <span className="text-xs text-text-dim">{issue.suggested_fix}</span>
                  </div>
                ) : null}
                {issue.proof_test_case ? (
                  <div
                    className="mt-2 bg-panel-3 border border-border rounded p-2.5 font-mono text-[11px] text-text"
                    data-testid="proof-of-issue"
                  >
                    <div className="text-warn font-semibold text-[10.5px] uppercase tracking-wider font-sans mb-1">
                      Proof-of-issue test case
                    </div>
                    {issue.proof_test_case}
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
      ))}
    </div>
  );
}


export function TesterRenderer({ output }: { output: TesterOutput }): JSX.Element {
  const DET_CLASS: Record<DeterminismClassification, string> = {
    deterministic: 'bg-ok/15 text-ok',
    flaky: 'bg-warn/15 text-warn',
    nondeterministic: 'bg-err/15 text-err'
  };
  const gate = output.verification_gate;
  const summary = output.summary;
  return (
    <div data-testid="role-tester">
      <RoleHeader role="tester" title="Test execution records" />
      {gate ? (
        <div
          className={`mb-3 rounded border px-3 py-2 ${
            gate.passed
              ? 'border-ok/40 bg-ok/10'
              : 'border-err/40 bg-err/10'
          }`}
          data-testid="verification-gate"
        >
          <div className="flex items-center gap-2 text-xs font-semibold">
            <span className={gate.passed ? 'text-ok' : 'text-err'}>
              {gate.passed ? '✓ Verification gate passed' : '✗ Verification gate FAILED'}
            </span>
            {!gate.evidence_available ? (
              <span className="text-warn text-[11px]">(unverified — no workspace)</span>
            ) : null}
          </div>
          {!gate.passed && gate.reason ? (
            <p className="mt-1 text-[11px] text-text-muted font-mono">{gate.reason}</p>
          ) : null}
          {gate.steps.length > 0 ? (
            <div className="mt-2 flex flex-col gap-0.5">
              {gate.steps.map((s, i) => (
                <div
                  key={`${s.name}-${i}`}
                  className="grid grid-cols-[16px_90px_1fr_70px] gap-2 items-center text-[11px]"
                  data-testid="gate-step"
                >
                  <span
                    className={
                      s.skipped ? 'text-text-muted' : s.passed ? 'text-ok' : 'text-err'
                    }
                  >
                    {s.skipped ? '○' : s.passed ? '✓' : '✗'}
                  </span>
                  <span className="font-semibold uppercase">{s.name}</span>
                  <span className="font-mono text-text-muted truncate">{s.command}</span>
                  <span className="text-text-muted text-right">
                    {s.skipped
                      ? 'skipped'
                      : s.exit_code === null
                        ? '—'
                        : `exit ${s.exit_code}`}
                    {!s.required ? ' · advisory' : ''}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {summary ? (
        <div
          className="rounded border border-border bg-surface-2 px-3 py-2 text-xs"
          data-testid="tester-summary"
        >
          <div className="mb-1.5 font-semibold text-text-muted uppercase text-[10px] tracking-wide">
            Test summary
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span>
              <span className="text-text-muted">Total</span> {summary.total_tests}
            </span>
            <span className="text-ok">✓ {summary.passing_tests} passing</span>
            {summary.failing_tests > 0 ? (
              <span className="text-err" data-testid="summary-failing">
                ✗ {summary.failing_tests} failing
              </span>
            ) : null}
            {summary.flaky_tests > 0 ? (
              <span className="text-warn">~ {summary.flaky_tests} flaky</span>
            ) : null}
            {summary.unverified_tests > 0 ? (
              <span className="text-text-muted" data-testid="summary-unverified">
                ○ {summary.unverified_tests} unverified
              </span>
            ) : null}
            <span className="text-text-muted">
              framework: <span className="font-mono">{summary.framework}</span>
            </span>
          </div>
          {summary.unverified_tests > 0 ? (
            <p className="mt-1.5 text-[11px] text-text-muted">
              Unverified tests ran but produced no per-test report — the verification
              gate above carries the authoritative pass/fail verdict.
            </p>
          ) : null}
        </div>
      ) : null}
      {output.records.length > 0
        ? output.records.map((r, i) => (
            <div
              key={`${r.test_name}-${i}`}
              className="grid grid-cols-[40px_1fr_120px_80px_70px] gap-2.5 px-3 py-2 border-b border-border text-xs items-center"
              data-testid="tester-row"
            >
              <span
                className={
                  r.mark === 'pass'
                    ? 'text-ok'
                    : r.mark === 'fail'
                      ? 'text-err'
                      : 'text-text-muted'
                }
                aria-label={r.mark}
              >
                {r.mark === 'pass' ? '✓' : r.mark === 'fail' ? '✗' : '○'}
              </span>
              <span className="font-mono text-[11.5px]">{r.test_name}</span>
              <span>
                {r.determinism_classification ? (
                  <span
                    className={`text-[10px] px-1.5 rounded font-semibold uppercase ${
                      DET_CLASS[r.determinism_classification]
                    }`}
                    data-testid={`det-${r.determinism_classification}`}
                  >
                    {r.determinism_classification === 'nondeterministic'
                      ? 'non-det'
                      : r.determinism_classification}
                  </span>
                ) : (
                  <span className="text-text-muted text-[11px]">— skip —</span>
                )}
              </span>
              <span className="text-text-muted text-[11px]">{r.duration_ms} ms</span>
              <span>
                {r.failure_trace_ref ? (
                  <button type="button" className="text-err text-[11px]" data-testid="trace-link">
                    trace →
                  </button>
                ) : null}
              </span>
            </div>
          ))
        : null}
    </div>
  );
}


function DocContentBlock({
  path,
  changeType,
  sections,
  content
}: {
  path: string;
  changeType?: string;
  sections?: ReadonlyArray<string>;
  content: string;
}): JSX.Element {
  return (
    <div className="mb-3.5" data-testid="doc-content">
      <div className="text-[11.5px] text-text-muted uppercase tracking-wider mb-1.5 flex items-center gap-2 flex-wrap normal-case">
        <span className="font-mono text-text">{path}</span>
        {changeType ? (
          <span className="inline-block bg-panel-2 border border-border text-[10.5px] px-1.5 rounded font-medium uppercase tracking-wider">
            {changeType}
          </span>
        ) : null}
        {sections && sections.length > 0 ? (
          <span className="text-[10.5px] text-text-muted">sections: {sections.join(', ')}</span>
        ) : null}
      </div>
      <pre className="text-[11.5px] leading-relaxed whitespace-pre-wrap break-words p-2.5 border border-border rounded-md bg-panel-2 text-text-dim overflow-x-auto">
        {content}
      </pre>
    </div>
  );
}

export function DocumenterRenderer({ output }: { output: DocumenterOutput }): JSX.Element {
  const readmeUpdates = output.readme_updates ?? [];
  const docUpdates = output.doc_updates ?? [];
  const docComments = output.doc_comments_added ?? [];
  const warnings = output.warnings ?? [];
  const docDiffs = output.doc_diffs ?? [];
  const fileCount = readmeUpdates.length + docUpdates.length;
  const hasContent = fileCount > 0 || docComments.length > 0;

  return (
    <div data-testid="role-documenter">
      <RoleHeader
        role="documenter"
        title={
          hasContent
            ? `Documentation — ${fileCount} ${fileCount === 1 ? 'file' : 'files'}${
                docComments.length > 0 ? ` · ${docComments.length} doc comments` : ''
              }`
            : `Doc updates — ${docDiffs.length} files · ${docDiffs.reduce(
                (n, d) => n + d.stale_flags.length,
                0
              )} stale flags`
        }
      />

      {warnings.length > 0 ? (
        <div
          data-testid="documenter-warnings"
          className="mb-3 px-3 py-2 rounded-md text-[11.5px] text-text-dim bg-warn/10 border-l-[3px] border-warn"
        >
          <div className="text-[10px] text-warn uppercase tracking-wider mb-1 font-medium">
            Notes from the documenter
          </div>
          {warnings.map((w, i) => (
            <div key={i} className="mb-0.5">
              {w}
            </div>
          ))}
        </div>
      ) : null}

      {readmeUpdates.map((r) => (
        <DocContentBlock
          key={`readme:${r.path}`}
          path={r.path}
          changeType={r.change_type}
          sections={r.sections_changed}
          content={r.content}
        />
      ))}

      {docUpdates.map((d) => (
        <DocContentBlock
          key={`doc:${d.path}`}
          path={d.path}
          changeType={d.change_type}
          content={d.content}
        />
      ))}

      {docComments.length > 0 ? (
        <div className="mb-3.5" data-testid="doc-comments">
          <div className="text-[11.5px] text-text-muted uppercase tracking-wider mb-1.5">
            Doc comments added — {docComments.length}
          </div>
          {docComments.map((c, i) => (
            <div key={i} className="mb-2">
              <div className="text-[11px] text-text-muted mb-1">
                <span className="font-mono text-text">{c.file_path}</span>
                {c.symbol ? <span className="ml-2">· {c.symbol}</span> : null}
              </div>
              <pre className="text-[11.5px] leading-relaxed whitespace-pre-wrap break-words p-2.5 border border-border rounded-md bg-panel-2 text-text-dim overflow-x-auto">
                {c.comment_block}
              </pre>
            </div>
          ))}
        </div>
      ) : null}

      {!hasContent
        ? docDiffs.map((d) => (
            <div key={d.doc_path} className="mb-3.5" data-testid="doc-diff">
              <div className="text-[11.5px] text-text-muted uppercase tracking-wider mb-1.5">
                {d.doc_path} diff
                {d.stale_flags.length > 0 ? (
                  <span className="inline-block bg-err/15 text-err text-[10.5px] px-1.5 rounded font-medium ml-2 normal-case">
                    {d.stale_flags.length} stale{' '}
                    {d.stale_flags.length === 1 ? 'reference' : 'references'} resolved
                  </span>
                ) : null}
              </div>
              <div className="bg-panel border border-border rounded-md overflow-hidden font-mono text-[11.5px] leading-relaxed">
                {d.diff.map((line, i) => (
                  <DiffRow key={i} line={line} />
                ))}
              </div>
            </div>
          ))
        : null}

      {!hasContent && docDiffs.length === 0 && warnings.length === 0 ? (
        <div className="p-4 text-text-muted text-[12px]">No documentation changes recorded.</div>
      ) : null}
    </div>
  );
}


export function RoleOutputRenderer({ output }: { output: RoleOutput }): JSX.Element {
  switch (output.kind) {
    case 'explorer':
      return <ExplorerRenderer output={output} />;
    case 'architect':
      return <ArchitectRenderer output={output} />;
    case 'coder':
      return <CoderRenderer output={output} />;
    case 'reviewer':
      return <ReviewerRenderer output={output} />;
    case 'tester':
      return <TesterRenderer output={output} />;
    case 'documenter':
      return <DocumenterRenderer output={output} />;
  }
}
